/**
 * materials.ts - Materiały i ich właściwości dla modułów inżynierskich
 * PlexiSystem Engineering Module
 */

export type MaterialKey = "PMMA" | "PETG" | "HIPS" | "PC" | "PVC" | "ABS";

export interface MaterialProperties {
  E_MPa: [number, number];         // Moduł Younga zakres [min, max]
  nu: [number, number];             // Współczynnik Poissona
  sigma_allow_MPa: [number, number]; // Naprężenia dopuszczalne (użytkowe, długookresowe)
  alpha_1e5_perK: [number, number];  // Współczynnik rozszerzalności *1e-5/K
  temp_use_C: [number, number];      // Zalecana temperatura użytkowa
  density_kg_m3: number;            // Gęstość materiału
  impact_kJ_m2?: number;            // Udarność (opcjonalne)
  uv_resistant: boolean;            // Odporność UV
  food_safe: boolean;               // Dopuszczenie do kontaktu z żywnością
}

export const MATERIALS: Record<MaterialKey, MaterialProperties> = {
  PMMA: {
    E_MPa: [2100, 3300],
    nu: [0.30, 0.38],
    sigma_allow_MPa: [25, 40],
    alpha_1e5_perK: [7, 9],
    temp_use_C: [0, 70],
    density_kg_m3: 1190,
    impact_kJ_m2: 2,
    uv_resistant: true,
    food_safe: true
  },
  PETG: {
    E_MPa: [1600, 2300],
    nu: [0.30, 0.38],
    sigma_allow_MPa: [30, 40],
    alpha_1e5_perK: [6, 7],
    temp_use_C: [0, 80],
    density_kg_m3: 1270,
    impact_kJ_m2: 8,
    uv_resistant: false,
    food_safe: true
  },
  HIPS: {
    E_MPa: [1400, 1600],
    nu: [0.30, 0.38],
    sigma_allow_MPa: [20, 30],
    alpha_1e5_perK: [7, 10],
    temp_use_C: [0, 70],
    density_kg_m3: 1050,
    impact_kJ_m2: 4,
    uv_resistant: false,
    food_safe: false
  },
  PC: {
    E_MPa: [2200, 2400],
    nu: [0.30, 0.38],
    sigma_allow_MPa: [35, 45],
    alpha_1e5_perK: [6, 7],
    temp_use_C: [0, 115],
    density_kg_m3: 1200,
    impact_kJ_m2: 30,
    uv_resistant: true,
    food_safe: true
  },
  PVC: {
    E_MPa: [2400, 3400],
    nu: [0.38, 0.40],
    sigma_allow_MPa: [40, 50],
    alpha_1e5_perK: [5, 8],
    temp_use_C: [0, 60],
    density_kg_m3: 1380,
    impact_kJ_m2: 2,
    uv_resistant: true,
    food_safe: false
  },
  ABS: {
    E_MPa: [2000, 2500],
    nu: [0.35, 0.38],
    sigma_allow_MPa: [30, 40],
    alpha_1e5_perK: [8, 10],
    temp_use_C: [0, 85],
    density_kg_m3: 1040,
    impact_kJ_m2: 20,
    uv_resistant: false,
    food_safe: false
  }
};

// Funkcja pomocnicza do pobierania średniej wartości
export function getMaterialValue(
  material: MaterialKey,
  property: keyof MaterialProperties,
  useMin = false,
  useMax = false
): number {
  const mat = MATERIALS[material];
  const value = mat[property];
  
  if (typeof value === 'number') return value;
  if (typeof value === 'boolean') return value ? 1 : 0;
  
  if (Array.isArray(value)) {
    if (useMin) return value[0];
    if (useMax) return value[1];
    return (value[0] + value[1]) / 2; // średnia
  }
  
  return 0;
}

// Funkcja do sprawdzenia kompatybilności materiału z wymaganiami
export function checkMaterialCompatibility(
  material: MaterialKey,
  requirements: {
    temperature?: number;
    foodContact?: boolean;
    uvExposure?: boolean;
    minImpact?: number;
  }
): { compatible: boolean; warnings: string[] } {
  const mat = MATERIALS[material];
  const warnings: string[] = [];
  let compatible = true;

  if (requirements.temperature !== undefined) {
    if (requirements.temperature < mat.temp_use_C[0] || requirements.temperature > mat.temp_use_C[1]) {
      warnings.push(`Temperatura ${requirements.temperature}°C poza zakresem użytkowym materiału ${material} (${mat.temp_use_C[0]}-${mat.temp_use_C[1]}°C)`);
      compatible = false;
    }
  }

  if (requirements.foodContact && !mat.food_safe) {
    warnings.push(`Materiał ${material} nie posiada dopuszczenia do kontaktu z żywnością`);
    compatible = false;
  }

  if (requirements.uvExposure && !mat.uv_resistant) {
    warnings.push(`Materiał ${material} wymaga stabilizacji UV lub powłoki ochronnej`);
  }

  if (requirements.minImpact && mat.impact_kJ_m2) {
    if (mat.impact_kJ_m2 < requirements.minImpact) {
      warnings.push(`Udarność materiału ${material} (${mat.impact_kJ_m2} kJ/m²) poniżej wymaganej (${requirements.minImpact} kJ/m²)`);
      compatible = false;
    }
  }

  return { compatible, warnings };
}
